import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, ArrowLeft, Users } from 'lucide-react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  getSuperAdminUsersApi,
  createSuperAdminUserApi,
  updateSuperAdminUserApi,
  deleteSuperAdminUserApi,
  getSuperAdminOrganizationApi,
  getSuperAdminCompaniesApi,
} from '../../api/api';
import { showMessage } from '../../context/MessageContext.jsx';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import { usePagination } from '../../hooks/usePagination.js';
import Select from 'react-select';
import './UsersList.css';

// Helper function to format date in YYYY-MM-DD format using local time (not UTC)
const formatDateForAPI = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date in DD-MM-YYYY format for display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export default function UsersList() {
  const { organizationId } = useParams(); // Backend expects organizationId (organization's id, not tallyId)
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    tallyUserId: '',
    userName: '',
    userEmail: '',
    password: '',
    contactNumber: '',
    aadharNumber: '',
    isOwner: false,
    companyIds: [],
    startDate: null,
    endDate: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;
  const { totalPages, goToPage, goToNextPage, goToPreviousPage } = usePagination(
    totalCount,
    limit,
    currentPage,
    setCurrentPage
  );

  useEffect(() => {
    fetchOrganization();
    fetchCompanies();
    fetchUsers();
  }, [organizationId, currentPage, search]);

  const fetchOrganization = async () => {
    try {
      const res = await getSuperAdminOrganizationApi(organizationId);
      if (res.data?.data) {
        setOrganization(res.data.data);
      } else {
        showMessage('Organization not found', 'error');
        navigate('/superadmin/organizations');
      }
    } catch (err) {
      showMessage('Failed to load organization', 'error');
      navigate('/superadmin/organizations');
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await getSuperAdminCompaniesApi(organizationId, { limit: 1000 });
      setCompanies(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: limit,
        ...(search && { search }),
      };
      const res = await getSuperAdminUsersApi(organizationId, params);
      setUsers(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch (err) {
      showMessage('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      // Map assignedCompanyIds to react-select format
      const assignedCompanies = (user.assignedCompanyIds || []).map(companyId => {
        const company = companies.find(c => c.companyId === companyId);
        return {
          value: companyId,
          label: company ? `${company.companyName} (${company.companyId})` : companyId
        };
      });
      
      setFormData({
        tallyUserId: user.tallyUserId || user.tallyUserId || '',
        userName: user.userName || '',
        userEmail: user.userEmail || '',
        password: '',
        contactNumber: user.contactNumber || '',
        aadharNumber: user.aadharNumber || '',
        isOwner: user.isOwner || false,
        companyIds: assignedCompanies,
        startDate: user.startDate ? new Date(user.startDate) : null,
        endDate: user.endDate ? new Date(user.endDate) : null,
      });
    } else {
      setEditingUser(null);
      setFormData({
        tallyUserId: '',
        userName: '',
        userEmail: '',
        password: '',
        contactNumber: '',
        aadharNumber: '',
        isOwner: false,
        companyIds: [],
        startDate: null,
        endDate: null,
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      tallyUserId: '',
      userName: '',
      userEmail: '',
      password: '',
      contactNumber: '',
      aadharNumber: '',
      isOwner: false,
      companyIds: [],
      startDate: null,
      endDate: null,
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    const errors = {};
    if (!formData.tallyUserId.trim()) errors.tallyUserId = 'User ID is required';
    if (!formData.userName.trim()) errors.userName = 'User Name is required';
    if (!editingUser && !formData.password.trim()) errors.password = 'Password is required';
    if (!formData.companyIds || !Array.isArray(formData.companyIds) || formData.companyIds.length === 0) {
      errors.companyIds = 'At least one company must be selected';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      // Support both new and old field names for backward compatibility
      const payload = {
        tallyUserId: formData.tallyUserId,
        userName: formData.userName,
        userEmail: formData.userEmail,
        password: formData.password,
        contactNumber: formData.contactNumber,
        aadharNumber: formData.aadharNumber,
        isOwner: formData.isOwner,
        companyIds: formData.companyIds.map((id) => id.value || id),
        startDate: formData.startDate ? formatDateForAPI(formData.startDate) : null,
        endDate: formData.endDate ? formatDateForAPI(formData.endDate) : null,
      };
      if (editingUser) {
        const userId = editingUser.tallyUserId || editingUser.tallyUserId;
        await updateSuperAdminUserApi(organizationId, userId, payload);
        showMessage('User updated successfully', 'success');
      } else {
        await createSuperAdminUserApi(organizationId, payload);
        showMessage('User created successfully', 'success');
      }
      handleCloseModal();
      fetchUsers();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 'Operation failed';
      showMessage(errorMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (user) => {
    const userName = user.userName;
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      return;
    }

    try {
      const userId = user.tallyUserId || user.tallyUserId;
      await deleteSuperAdminUserApi(organizationId, userId);
      showMessage('User deleted successfully', 'success');
      fetchUsers();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 'Failed to delete user';
      showMessage(errorMsg, 'error');
    }
  };

  const companyOptions = companies.map((company) => ({
    value: company.companyId,
    label: `${company.companyName} (${company.companyId})`,
  }));

  if (!organization) {
    return <div className="users-list-loading">Loading organization...</div>;
  }

  return (
    <div className="users-list">
      <div className="users-list-header">
        <div className="users-list-header-left">
          <Button
            variant="secondary"
            onClick={() => navigate('/superadmin/organizations')}
            className="users-list-back-button"
          >
            <ArrowLeft size={18} />
            Back
          </Button>
          <div>
            <h1 className="users-list-title">Users</h1>
            <p className="users-list-subtitle">
              Organization: {organization.ownerName} ({organization.tallyId})
            </p>
          </div>
        </div>
        <Button
          variant="primary"
          onClick={() => handleOpenModal()}
          className="users-list-add-button"
        >
          <Plus size={18} />
          Add User
        </Button>
      </div>

      <Card className="users-list-filters">
        <div className="users-list-search">
          <Search size={20} className="users-list-search-icon" />
          <Input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="users-list-search-input"
          />
        </div>
      </Card>

      {loading ? (
        <div className="users-list-loading">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : users.length === 0 ? (
        <Card className="users-list-empty">
          <Users size={48} />
          <p>No users found</p>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <Plus size={18} />
            Add First User
          </Button>
        </Card>
      ) : (
        <>
          <div className="users-list-table-container">
            <table className="users-list-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Contact</th>
                  <th>Owner</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const userId = u.tallyUserId || u.tallyUserId;
                  const userName = u.userName;
                  const userEmail = u.userEmail;
                  return (
                  <tr key={userId}>
                    <td>{userId}</td>
                    <td>{userName}</td>
                    <td>{userEmail || 'N/A'}</td>
                    <td>{u.contactNumber || 'N/A'}</td>
                    <td>
                      <span className={`users-list-badge ${u.isOwner ? 'active' : ''}`}>
                        {u.isOwner ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>{formatDateForDisplay(u.startDate)}</td>
                    <td>{formatDateForDisplay(u.endDate)}</td>
                    <td>
                      <span className={`users-list-badge ${u.status === 1 ? 'active' : 'inactive'}`}>
                        {u.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="users-list-actions">
                        <button
                          className="users-list-action"
                          onClick={() => handleOpenModal(u)}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="users-list-action danger"
                          onClick={() => handleDelete(u)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              onNextPage={goToNextPage}
              onPreviousPage={goToPreviousPage}
            />
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="users-list-modal-overlay" onClick={handleCloseModal}>
          <div className="users-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="users-list-modal-header">
              <h2>{editingUser ? 'Edit User' : 'Add User'}</h2>
              <button className="users-list-modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="users-list-modal-form">
              <FormField
                label="User ID *"
                error={formErrors.tallyUserId}
              >
                <Input
                  type="text"
                  value={formData.tallyUserId}
                  onChange={(e) => setFormData({ ...formData, tallyUserId: e.target.value })}
                  placeholder="Enter User ID"
                  disabled={!!editingUser}
                  required
                />
              </FormField>

              <FormField
                label="User Name *"
                error={formErrors.userName}
              >
                <Input
                  type="text"
                  value={formData.userName}
                  onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                  placeholder="Enter user name"
                  required
                />
              </FormField>

              <FormField
                label="Email"
                error={formErrors.userEmail}
              >
                <Input
                  type="email"
                  value={formData.userEmail}
                  onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
                  placeholder="Enter email"
                />
              </FormField>

              <FormField
                label={editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                error={formErrors.password}
              >
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  required={!editingUser}
                />
              </FormField>

              <div className="users-list-modal-row">
                <FormField
                  label="Contact Number"
                  error={formErrors.contactNumber}
                >
                  <Input
                    type="text"
                    value={formData.contactNumber}
                    onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
                    placeholder="Enter contact number"
                  />
                </FormField>

                <FormField
                  label="Aadhar Number"
                  error={formErrors.aadharNumber}
                >
                  <Input
                    type="text"
                    value={formData.aadharNumber}
                    onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })}
                    placeholder="Enter Aadhar number"
                  />
                </FormField>
              </div>

              <FormField label="Assign to Companies" required error={formErrors.companyIds}>
                <Select
                  isMulti
                  options={companyOptions}
                  value={formData.companyIds}
                  onChange={(selected) => {
                    setFormData({ ...formData, companyIds: selected || [] });
                    // Clear error when user selects a company
                    if (selected && selected.length > 0 && formErrors.companyIds) {
                      setFormErrors({ ...formErrors, companyIds: '' });
                    }
                  }}
                  placeholder="Select companies"
                  className="users-list-select"
                  classNamePrefix="users-list-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: formErrors.companyIds ? '#dc3545' : base.borderColor,
                      '&:hover': {
                        borderColor: formErrors.companyIds ? '#dc3545' : base.borderColor,
                      },
                    }),
                  }}
                />
              </FormField>

              <div className="users-list-modal-row">
                <FormField label="Start Date">
                  <ReactDatePicker
                    selected={formData.startDate}
                    onChange={(date) => setFormData({ ...formData, startDate: date })}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="DD-MM-YYYY"
                    className="users-list-datepicker"
                    isClearable
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                  />
                </FormField>

                <FormField label="End Date">
                  <ReactDatePicker
                    selected={formData.endDate}
                    onChange={(date) => setFormData({ ...formData, endDate: date })}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="DD-MM-YYYY"
                    className="users-list-datepicker"
                    minDate={formData.startDate}
                    isClearable
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                  />
                </FormField>
              </div>

              <div className="users-list-modal-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isOwner}
                    onChange={(e) => setFormData({ ...formData, isOwner: e.target.checked })}
                  />
                  <span>Is Owner</span>
                </label>
              </div>

              <div className="users-list-modal-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={submitting}
                >
                  {editingUser ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

